<template>
  <div>
    <Hero :title="heroTitle" :subtitle="categoryDescription" />

    <!-- Show all the templates -->
    <Section>
      <div class="p-4 mb-6 bg-white rounded-lg md:p-6 md:mb-8">
        <!-- Show when templates are loading -->
        <Loader v-if="showLoader" label="Loading Templates..." />

        <div
          v-if="!$apollo.loading && templates.length && !errors.length"
          class="breadcrumbs"
        >
          <router-link to="/templates" title="👈 Back to all templates"
            >Templates</router-link
          >
          <v-icon name="chevron-right" scale="0.7" class="ml-1" />
          {{ categoryLabel }}
        </div>

        <div
          v-if="!$apollo.loading && templates.length && !errors.length"
          class="templates-preview-wrap"
        >
          <TemplatePreviewCard
            v-for="template in templates"
            :id="template.baseTemplateId"
            :key="template.baseTemplateId"
            :name="template.name"
            :to="`${path}/${template.baseTemplateId}`"
            :img-url="$get(template, 'previewImage.src')"
          />
        </div>
      </div>

      <!-- Load more items as the user scrolls -->
      <!-- <InfiniteLoader
        v-if="!$apollo.loading && paginationKey"
        v-show="!$apollo.loading && !loadingMore"
        :load="fetchMoreElements"
        class="mx-auto mt-3 mb-6 grey xs"
        :loading="loadingMore"
      >
        {{ loadingMore ? "Loading..." : "Load more..." }}
      </InfiniteLoader> -->

      <div class="mt-2 mb-4 divider md:mt-4 lg:mt-6" />
    </Section>
  </div>
</template>

<script>
import { BaseTemplates } from "@/graphql/queries";
import TemplatePreviewCard from "@/components/Templates/TemplatePreviewCard";
import InfiniteLoader from "@/components/Utils/InfiniteLoader/InfiniteLoader";
import Loader from "@/components/UI/Loader";
import Hero from "@/components/UI/Hero";
import Section from "@/components/UI/Section";

const PAGINATION_LIMIT = 20;

export default {
  name: "TemplateCategory",
  components: { Hero, Loader, Section, TemplatePreviewCard },
  apollo: {
    $query: {
      fetchPolicy: "cache-first",
    },
    baseTemplates: {
      query: BaseTemplates,
      error(error) {
        this.errors.push(error);
      },
      variables: function() {
        return {
          input: {
            categoryId: this.$route.params.category,
            paginationKey: "",
            limit: PAGINATION_LIMIT,
          },
        };
      },
      manual: true, // disable the automatic property update for pagination to work. Need to specify a result callback...
      result({ data, loading }) {
        if (!loading) {
          this.templates = this.$get(data, "baseTemplates.baseTemplates", []);
          this.paginationKey = this.$get(
            data,
            "baseTemplates.paginationKey",
            ""
          );
        }
      },
    },
  },
  data() {
    return {
      errors: [],
      loadingMore: false,
      paginationKey: "",
      templates: [],
    };
  },
  computed: {
    categoryDescription() {
      return this.templates[0]?.category?.description;
    },
    categoryLabel() {
      return this.templates[0]?.category?.label ?? "";
    },
    heroTitle() {
      return this.categoryLabel ? `${this.categoryLabel} Emails` : "";
    },
    path() {
      return `/templates/${this.$route.params.category}`;
    },
    showLoader() {
      // handle the initial loading state...
      if (this.$apollo.loading && !this.errors.length && !this.templates.length)
        return true;

      return false;
    },
  },
  mounted() {
    // Make sure the user is at the top of the screen as sometimes the browser remembers their last known scroll postion
    window.scrollTo(0, 0);
  },
  methods: {
    fetchMoreElements() {
      const { type, paginationKey } = this;
      const variables = {
        input: {
          categoryId: this.$route.params.category,
          limit: PAGINATION_LIMIT,
          paginationKey: paginationKey,
        },
      };

      this.loadingMore = true;

      // Fetch more data and transform the original result
      this.$apollo.queries.baseTemplates.fetchMore({
        variables,
        // Transform the previous result with new data
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const previousItems = this.$get(
            previousResult,
            "baseTemplates.baseTemplates",
            []
          );
          const newItems = this.$get(
            fetchMoreResult,
            "baseTemplates.baseTemplates",
            []
          );
          const paginationKey = this.$get(
            fetchMoreResult,
            "baseTemplates.paginationKey",
            ""
          );

          this.loadingMore = false;

          return {
            baseTemplates: {
              __typename: previousResult.baseTemplates.__typename,
              // Merging the items...
              baseTemplates: [...previousItems, ...newItems],
              // Update the paginationKey...
              paginationKey,
            },
          };
        },
      });
    },
  },
};
</script>

<style scoped>
.breadcrumbs {
  @apply mb-4 text-xs text-grey-dark text-center;

  @screen md {
    @apply mb-6 text-sm;
  }

  @screen lg {
    @apply mb-8;
  }
}

.templates-preview-wrap {
  @apply flex flex-wrap justify-center -mx-2;

  @screen lg {
    @apply -mx-4;
  }

  @screen xl {
    @apply max-w-2xl mx-auto;
  }
}

>>> .template-preview-card {
  @apply mb-4 px-2 h-full;
  max-width: 233px;

  @screen sm {
    @apply w-1/2;
  }

  @screen md {
    @apply w-1/3;
  }

  @screen lg {
    @apply w-1/4 mb-8 px-4;
  }

  @screen xl {
    @apply mb-10;
  }
}
</style>
